import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';
import { Editor } from '@tinymce/tinymce-react';
import LottieAnim from '../components/LottieAnim';
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/plugins/code';
import 'tinymce/plugins/table';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/autolink';

import axios from 'axios';
import SectionTabs from '../components/SectionTabs';
import { CalendarDays, FilePlus, Mail, Package } from 'lucide-react';

function EmailToClientsPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(null);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');

  const [jobState, setJobState] = useState(null); // null | { status, total, done, success, failed }
  const pollRef = useRef(null);

  // 이미지 에셋 (서명 등)
  const editorRef = useRef(null);
  const assetFileRef = useRef(null);
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetUploading, setAssetUploading] = useState(false);

  const loadAssets = async () => {
    try {
      const res = await axios.get('/email/assets');
      setAssets(res.data.items || []);
    } catch (e) { console.error('에셋 목록 로드 실패:', e); }
  };

  const uploadAsset = async (file) => {
    if (!file) return;
    setAssetUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post('/email/assets', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadAssets();
    } catch (e) {
      alert(e.response?.data?.errorMessage || '업로드 실패');
    } finally {
      setAssetUploading(false);
      if (assetFileRef.current) assetFileRef.current.value = '';
    }
  };

  const deleteAsset = async (id) => {
    if (!window.confirm('이 에셋을 삭제할까요?')) return;
    try {
      await axios.delete(`/email/assets/${id}`);
      setAssets((prev) => prev.filter((a) => a.id !== id));
    } catch (e) { alert('삭제 실패'); }
  };

  const saveAssetWidth = async (id, widthStr) => {
    const width = widthStr === '' ? null : parseInt(widthStr, 10);
    if (width !== null && (isNaN(width) || width < 10 || width > 2000)) return;
    try {
      await axios.patch(`/email/assets/${id}`, { width });
      setAssets((prev) => prev.map((a) => (a.id === id ? { ...a, width } : a)));
    } catch (e) { alert('가로폭 저장 실패'); }
  };

  const insertAsset = (asset) => {
    if (editorRef.current) {
      const widthAttr = asset.width ? `width="${asset.width}" style="max-width:100%;"` : 'style="max-width:100%;"';
      editorRef.current.insertContent(`<img src="${asset.url}" alt="${asset.name}" ${widthAttr} />`);
    }
    setAssetModalOpen(false);
  };

  const templates = useMemo(
    () => [
      {
        name: '정산 오픈 공지',
        subject: '[p!nk] 2025년 12월분 정산 공지',
        body: `
        <p><strong>\${name}</strong>님 안녕하세요,<br>p!nk 이선하입니다.</p>
<p><br>유통을 맡겨주신 음원의 2026. 12 분배분 음원 정산 리포트가 반영 되었습니다.<br>(음원 : 2025. 11 판매분)<br>정산 시스템에 접속하시어 상세 내용 확인하실 수 있습니다.</p>
<p><br><br><span style="font-size: 14pt;"><strong>1. 사업자 권리자</strong></span></p>
<p>사업자 권리자께서는 하기 내용 참고하시어 세금계산서 발행 부탁 드립니다. <span style="color: #e03e2d;"><strong>(개인 권리자 해당X)</strong></span></p>
<table style="border-collapse: collapse; width: 450px;" border="1"><colgroup><col style="width: 41.9753%;"><col style="width: 58.0247%;"></colgroup>
<tbody>
<tr style="height: 33.3906px;">
<td style="text-align: center; background-color: #000000;"><span style="color: #ffffff;"><strong>구분</strong></span></td>
<td style="text-align: center; background-color: #000000;"><span style="color: #ffffff;"><strong>내용</strong></span></td>
</tr>
<tr style="height: 33.3906px;">
<td style="text-align: center; text-color: white;">품목명</td>
<td style="text-align: center;"><strong>2025. 12 음원 인세</strong></td>
</tr>
<tr style="height: 33.3906px;">
<td style="text-align: center;">발행일자</td>
<td style="text-align: center;">2025. 12. 31</td>
</tr>
<tr style="height: 33.3906px;">
<td style="text-align: center;">발행마감일</td>
<td style="text-align: center;"><span style="color: #e03e2d;"><strong>2026. 1. 10</strong></span></td>
</tr>
<tr style="height: 33.3906px;">
<td style="text-align: center;">세금계산서 수신 메일</td>
<td style="text-align: center;"><span style="color: #236fa1;"><strong>shlee@impink.co.kr</strong></span></td>
</tr>
<tr style="height: 35px;">
<td style="text-align: center;">지급일</td>
<td style="text-align: center;">2026. 1. 31</td>
</tr>
</tbody>
</table>
<p>※ 간이과세자께서는 현금영수증 발행하시어 <span style="color: #236fa1;"><strong>shlee@impink.co.kr</strong></span>로 <span style="color: #e03e2d;"><strong>전달</strong></span> 부탁 드립니다.</p>
<p><span style="font-size: 14pt;"><strong><br>2. 개인 권리자</strong></span></p>
<p>개인 권리자께서는 세금계산서 발행과 관계 없이 원천세 징수 후 지급일에 입금됩니다.</p>

<table style="width: 395px; height: 125.781px; border-collapse: collapse; border-width: 1px; border-spacing: 0px;" border="1" cellspacing="0" cellpadding="6">
<tbody>
<tr style="height: 125.781px;">
<td style="width: 57.1875px; text-align: center; background-color: #000000;"><span style="color: #ffffff;"><strong>정산<br>시스템</strong></span></td>
<td style="text-align: center; width: 324px;" colspan="2">
<p style="text-align: left;">&nbsp; &nbsp; 1. 정산사이트 : <strong>https://urpink.co.kr</strong></p>
<p style="text-align: left;">&nbsp; &nbsp; 2. ID : <strong>\${loginId}</strong></p>
<p style="text-align: left;">&nbsp; &nbsp; 3. PASSWORD : <strong>\${password}</strong><br>
<span style="text-align: left;">&nbsp; &nbsp; &nbsp; &nbsp; (난수 로그인 불가 시 <b>전화번호 뒤 4자리</b>,<br>
&nbsp; &nbsp; &nbsp; &nbsp; 직접 변경하셨을 경우 <b>변경하신 비밀번호</b>)</span></p>
</td>
</tr>
</tbody>
</table>
<p>&nbsp;</p>
<p><br>자세한 내용은 정산시스템을 확인 부탁 드리며,<br>정산 관련 문의사항이 있으실 경우 하단 서명의 메일 또는 전화번호로 연락 부탁 드립니다.</p>
<p>감사합니다.</p>
<p>&nbsp;</p>
<p>&nbsp;</p>
<p>&nbsp;</p>
<p><img src="https://ci3.googleusercontent.com/mail-sig/AIorK4we-jmMZlOlgemc4016ymEM1o74DnRHqB3PoIOwfqA--7Mix51gV9DtmJQfPnw6SYYvTlPiOh_GFtu9" width="420"></p>
      `,
      },
      {
        name: '일반 공지',
        subject: '[p!nk] 일반공지',
        body: `
        <p>안녕하세요 <b>\${name}</b>님,<br>
        p!nk 입니다.</p>
      `,
      },
    ],
    []
  );

  useEffect(() => {
    if (me && selectedTemplateIndex !== null) {
      const tpl = templates[selectedTemplateIndex];
      setSubject(tpl.subject);
      setBody(tpl.body);
    }
  }, [me, selectedTemplateIndex, templates]);

  useEffect(() => {
    axios
      .get('/client?limit=10000')
      .then((res) => {
        const data = Array.isArray(res.data.items) ? res.data.items : [];
        setClients(data);
        setFilteredClients(data);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFilteredClients(clients);
    } else {
      setFilteredClients(
        clients.filter(
          (c) =>
            c.loginId.toLowerCase().includes(search.toLowerCase()) ||
            (c.name && c.name.toLowerCase().includes(search.toLowerCase()))
        )
      );
    }
  }, [search, clients]);

  // 폴링 정리
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const toggleSelect = (loginId) => {
    const s = new Set(selected);
    s.has(loginId) ? s.delete(loginId) : s.add(loginId);
    setSelected(s);
  };

  const toggleAll = () => {
    if (filteredClients.every((c) => selected.has(c.loginId))) {
      const s = new Set(selected);
      filteredClients.forEach((c) => s.delete(c.loginId));
      setSelected(s);
    } else {
      const s = new Set(selected);
      filteredClients.forEach((c) => s.add(c.loginId));
      setSelected(s);
    }
  };

  const handleSend = async () => {
    if (selected.size === 0) return alert('대상을 선택하세요');
    if (pollRef.current) clearInterval(pollRef.current);

    setJobState({ status: 'running', total: selected.size, done: 0, success: 0, failed: [] });

    try {
      const res = await axios.post('/email', {
        subject,
        html: body,
        targets: Array.from(selected),
      });

      const { jobId, total } = res.data;
      setJobState((prev) => ({ ...prev, total }));

      pollRef.current = setInterval(async () => {
        try {
          const prog = await axios.get(`/email/progress/${jobId}`);
          setJobState(prog.data);
          if (prog.data.status === 'done' || prog.data.status === 'error') {
            clearInterval(pollRef.current);
          }
        } catch {
          clearInterval(pollRef.current);
        }
      }, 1500);
    } catch (err) {
      setJobState({ status: 'error', message: err.message });
    }
  };

  const isSending = jobState?.status === 'running';
  const isDone = jobState?.status === 'done';
  const isError = jobState?.status === 'error';
  const progress = jobState ? Math.round(((jobState.done || 0) / (jobState.total || 1)) * 100) : 0;

  return (
    <>
      <div className="flex mb-8 justify-center items-center">
        <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">권리사 전체메일 발송</div>
      </div>
      <SectionTabs items={[
        { label: '음원 월정산', to: '/MonthlyUpload', icon: CalendarDays },
        { label: '음반 월정산', to: '/PhysicalUpload', icon: Package },
        { label: '대량등록', to: '/BigDataUpload', icon: FilePlus },
        { label: '권리사메일발송', to: '/EmailToClients', icon: Mail },
      ]} />

        <select
          className="rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 px-3 py-2 mt-8 mb-4 w-full text-sm focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent"
          value={selectedTemplateIndex ?? ''}
          onChange={(e) => setSelectedTemplateIndex(Number(e.target.value))}>
          <option value="" disabled>메일 템플릿 선택</option>
          {templates.map((tpl, i) => (
            <option key={i} value={i}>{tpl.name}</option>
          ))}
        </select>

        <input
          className="rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 px-3 py-2 mb-4 w-full text-sm focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent"
          type="text"
          placeholder="메일 제목"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />

        <Editor
          value={body}
          onInit={(evt, editor) => { editorRef.current = editor; }}
          init={{
            height: 500,
            menubar: false,
            toolbar:
              'undo redo | formatselect fontsize | bold italic underline forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist | table | link | imageassets | code',
            plugins: ['code', 'table', 'lists', 'link', 'autolink'],
            content_style:
              'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
            fontsize_formats: '8pt 10pt 12pt 14pt 18pt 24pt 36pt',
            base_url: '/tinymce',
            license_key: 'gpl',
            setup: (editor) => {
              editor.ui.registry.addButton('imageassets', {
                icon: 'image',
                tooltip: '이미지 에셋 (서명 등)',
                onAction: () => {
                  loadAssets();
                  setAssetModalOpen(true);
                },
              });
            },
          }}
          onEditorChange={(newValue) => setBody(newValue)}
        />

        {/* 이미지 에셋 모달 */}
        {assetModalOpen && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setAssetModalOpen(false)}>
            <div className="bg-[#13233c] rounded-2xl border border-white/10 w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <h3 className="text-white font-semibold text-sm">이미지 에셋</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => assetFileRef.current?.click()}
                    disabled={assetUploading}
                    className="px-3 py-1.5 rounded-lg bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 text-xs font-medium transition-colors disabled:opacity-50">
                    {assetUploading ? '업로드 중...' : '+ 새 이미지 업로드'}
                  </button>
                  <button onClick={() => setAssetModalOpen(false)} className="px-2 py-1.5 text-slate-400 hover:text-white text-sm">✕</button>
                </div>
              </div>
              <input ref={assetFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset(e.target.files[0])} />
              <div className="p-5 overflow-y-auto">
                {assets.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-10">저장된 에셋이 없습니다. 서명 이미지 등을 업로드해보세요.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {assets.map((a) => (
                      <div key={a.id} className="group relative rounded-xl border border-white/10 bg-white/5 overflow-hidden">
                        <button onClick={() => insertAsset(a)} className="block w-full" title="클릭하여 본문에 삽입">
                          <div className="h-28 flex items-center justify-center bg-white p-2">
                            <img src={a.url} alt={a.name} className="max-h-full max-w-full object-contain" />
                          </div>
                          <p className="px-2 py-1.5 text-[11px] text-slate-400 truncate text-left">{a.name}</p>
                        </button>
                        <div className="flex items-center gap-1 px-2 pb-2" onClick={(e) => e.stopPropagation()}>
                          <span className="text-[10px] text-slate-500">가로</span>
                          <input
                            type="number"
                            defaultValue={a.width || ''}
                            placeholder="원본"
                            min={10}
                            max={2000}
                            onBlur={(e) => saveAssetWidth(a.id, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            className="w-16 px-1.5 py-0.5 rounded bg-black/30 border border-white/10 text-slate-200 text-[11px] focus:outline-none focus:border-sky-400"
                          />
                          <span className="text-[10px] text-slate-500">px</span>
                        </div>
                        <button
                          onClick={() => deleteAsset(a.id)}
                          className="absolute top-1 right-1 hidden group-hover:block px-1.5 py-0.5 rounded bg-black/70 text-red-400 text-xs"
                          title="삭제">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="px-5 pb-4 text-[11px] text-slate-500">이미지를 클릭하면 커서 위치에 삽입됩니다. 이메일 서명은 가로 500px 내외를 권장합니다.</p>
            </div>
          </div>
        )}

        <div className="flex w-full justify-between items-center my-4">
          <input
            className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent"
            placeholder="ID 또는 이름 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            className="ml-3 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-slate-300 text-sm whitespace-nowrap transition-colors"
            onClick={toggleAll}>
            전체 선택/해제
          </button>
          <span className="hidden lg:inline-block ml-4 text-sm text-slate-400 whitespace-nowrap">
            선택된 권리사 수: {selected.size}
          </span>
        </div>

        <div className="overflow-y-auto max-h-[400px] rounded-2xl border border-white/[0.08] my-4">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] sticky top-0">
              <tr className="border-b border-white/[0.08]">
                <th className="px-3 py-3 font-semibold text-slate-400 text-xs uppercase tracking-wide whitespace-nowrap">선택</th>
                <th className="px-3 py-3 font-semibold text-slate-400 text-xs uppercase tracking-wide whitespace-nowrap">ID</th>
                <th className="px-3 py-3 font-semibold text-slate-400 text-xs uppercase tracking-wide whitespace-nowrap">이름</th>
                <th className="px-3 py-3 font-semibold text-slate-400 text-xs uppercase tracking-wide whitespace-nowrap">이메일</th>
              </tr>
            </thead>
            <tbody className="bg-[#182334] divide-y divide-white/[0.05]">
              {filteredClients.map((c) => {
                const email = `${c.emailId}@${c.emailDomain}`;
                const checked = selected.has(c.loginId);
                return (
                  <tr key={c.loginId} className="hover:bg-white/[0.03] transition-colors">
                    <td className="px-3 py-3 text-center">
                      <input type="checkbox" checked={checked} onChange={() => toggleSelect(c.loginId)} />
                    </td>
                    <td className="px-3 py-3 text-slate-200">{c.loginId}</td>
                    <td className="px-3 py-3 text-slate-200">{c.name}</td>
                    <td className="px-3 py-3 text-slate-200">{email}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col w-full items-end mt-5">
          <button
            className="px-4 py-2 rounded-xl bg-sky-500/20 hover:bg-sky-500/30 text-sky-400 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            onClick={handleSend}
            disabled={isSending}>
            {isSending ? '발송 중...' : '선택 대상에게 발송'}
          </button>

          {/* 진행 상황 */}
          {jobState && (isSending || isDone || isError) && (
            <div className="mt-4 w-full">
              {/* 프로그레스 바 */}
              {(isSending || isDone) && (
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-slate-400 mb-1">
                    <span>{isSending ? '발송 중...' : '발송 완료'}</span>
                    <span>{jobState.done || 0} / {jobState.total}명 ({progress}%)</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${isDone ? 'bg-green-500' : 'bg-sky-500'}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* 완료 결과 */}
              {isDone && (
                <div className="text-sm text-green-400">
                  <div className="flex items-center gap-3">
                    <LottieAnim type="success" size={60} />
                    <span>발송 완료 — 성공 {jobState.success}명 / 전체 {jobState.total}명</span>
                  </div>
                  {jobState.failed?.length > 0 && (
                    <div className="mt-2 text-red-400">
                      <div>실패 {jobState.failed.length}명:</div>
                      <ul className="list-disc list-inside mt-1 space-y-0.5">
                        {jobState.failed.map((f) => (
                          <li key={f.loginId}>{f.loginId} ({f.email}) — {f.reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* 오류 */}
              {isError && (
                <div className="text-sm text-red-400">
                  ❌ 발송 오류: {jobState.message}
                </div>
              )}
            </div>
          )}
        </div>
    </>
  );
}

export default EmailToClientsPage;
